import type { SurveyAttribute, SurveyAttributeOptionScores } from '@engagement-web-app/common';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Link } from '@/components/Link';
import { Section, SectionHeader } from '@/components/Section';
import { Ranking, RankingSkeleton } from '@/components/surveys/common/Ranking';

type RankingsByDepartmentProps = {
    organizationId: string;
    /** `null` while still loading. */
    attributes: SurveyAttribute[] | null;
    /** `null` while still loading. */
    scores: SurveyAttributeOptionScores[] | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

export function RankingsByDepartment({
    organizationId,
    attributes,
    scores,
    isError,
    onRetry,
}: RankingsByDepartmentProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message="組織別ランキングの取得に失敗しました。" />;

    if (!attributes) return <RankingsByDepartmentSkeleton />;

    // Resolved before `scores` is checked: whether the organization has a department
    // attribute at all is known as soon as `attributes` lands and isn't a loading
    // state. The scores query is also disabled without a setting id, so `scores`
    // would stay null forever here and the skeleton would never resolve.
    const departmentSetting = attributes.find((s) => s.type === 'DEPARTMENT');
    if (!departmentSetting) return <NoDepartmentSettingNotice organizationId={organizationId} />;

    if (!scores) return <RankingsByDepartmentSkeleton />;

    return <RankingsByDepartmentContent scores={scores} />;
}

type RankingsByDepartmentContentProps = {
    scores: SurveyAttributeOptionScores[];
};

function RankingsByDepartmentContent({ scores }: RankingsByDepartmentContentProps) {
    return (
        <Flex gap="4">
            <Ranking title="よかった組織" variant="positive" items={scores} limit={10} />
            <Ranking title="改善必要組織" variant="negative" items={scores} limit={10} />
        </Flex>
    );
}

function NoDepartmentSettingNotice({ organizationId }: { organizationId: string }) {
    return (
        <Section>
            <SectionHeader title="組織別ランキング" />
            <Flex direction="column" justify="center" align="center">
                <div>『組織』に当たる属性が設定されていないため、ランキングを表示できません。</div>
                <Link to="/system/organizations/$organizationId/survey-settings" params={{ organizationId }}>
                    こちらからサーベイを選択して設定をしてください。
                </Link>
            </Flex>
        </Section>
    );
}

export function RankingsByDepartmentSkeleton() {
    return (
        <Flex gap="4">
            <RankingSkeleton limit={10} />
            <RankingSkeleton limit={10} />
        </Flex>
    );
}
